import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Filter, PenLine, RotateCcw, Search, Wrench } from "lucide-react";
import { Button, ButtonLink, Card, Input, Label, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { Link } from "../lib/router";

export function RatListPage() {
  const [draftBusca, setDraftBusca] = useState("");
  const [busca, setBusca] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [empresaAtendida, setEmpresaAtendida] = useState("");
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");

  const rats = useQuery({
    queryKey: ["rats", busca, tecnico, empresaAtendida, dataDe, dataAte],
    queryFn: () =>
      api.listRats({
        busca: busca || undefined,
        tecnico: tecnico || undefined,
        empresaAtendida: empresaAtendida || undefined,
        dataDe: dataDe || undefined,
        dataAte: dataAte || undefined,
      }),
  });

  function applyFilters(event: FormEvent) {
    event.preventDefault();
    setBusca(draftBusca.trim());
  }

  function clearFilters() {
    setDraftBusca("");
    setBusca("");
    setTecnico("");
    setEmpresaAtendida("");
    setDataDe("");
    setDataAte("");
  }

  const itens = rats.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="RAT"
        title="Relatórios de Atendimento Técnico"
        description="Atendimentos presenciais registrados pela equipe Mira Sistemas."
        actions={
          <ButtonLink to="/rat/novo">
            <Wrench className="size-4" />
            Novo RAT
          </ButtonLink>
        }
      />

      <Card className="form-section">
        <form onSubmit={applyFilters} className="form-grid">
          <div>
            <Label htmlFor="f-busca">Buscar (empresa, contato, chamado, protocolo...)</Label>
            <Input
              id="f-busca"
              value={draftBusca}
              onChange={(event) => setDraftBusca(event.target.value)}
              placeholder="Digite e pressione Buscar"
            />
          </div>
          <div>
            <Label htmlFor="f-tecnico">Técnico</Label>
            <Input id="f-tecnico" value={tecnico} onChange={(event) => setTecnico(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="f-empresa">Empresa atendida</Label>
            <Input
              id="f-empresa"
              value={empresaAtendida}
              onChange={(event) => setEmpresaAtendida(event.target.value)}
            />
          </div>
          <div />
          <div>
            <Label htmlFor="f-data-de">Data de atendimento (de)</Label>
            <Input id="f-data-de" type="date" value={dataDe} onChange={(event) => setDataDe(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="f-data-ate">Data de atendimento (até)</Label>
            <Input id="f-data-ate" type="date" value={dataAte} onChange={(event) => setDataAte(event.target.value)} />
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <Button type="submit">
              <Search className="size-4" />
              Buscar
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>
              <RotateCcw className="size-4" />
              Limpar filtros
            </Button>
          </div>
        </form>
      </Card>

      <Card className="form-section">
        {rats.isLoading ? (
          <p className="text-sm text-slate-500">Carregando RATs...</p>
        ) : itens.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-500">
            <Filter className="size-6" />
            <p className="text-sm">Nenhum RAT encontrado com esses filtros.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ticket-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Data</th>
                  <th>Técnico</th>
                  <th>Empresa atendida</th>
                  <th>Contato</th>
                  <th>Chamado</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <tr key={item.Id}>
                    <td>#{item.Id}</td>
                    <td>{new Date(item.DataAtendimento).toLocaleDateString("pt-BR")}</td>
                    <td>{item.NomeTecnico}</td>
                    <td>{item.EmpresaAtendida}</td>
                    <td>{item.NomeContatoCliente}</td>
                    <td>{item.ChamadoId ? `#${item.ChamadoId}` : "—"}</td>
                    <td>
                      {item.status === "rascunho" ? (
                        <span className="status-badge status-badge--warning">Em andamento</span>
                      ) : (
                        <span className="status-badge status-badge--success">Concluído</span>
                      )}
                    </td>
                    <td>
                      <Link to={`/rat/${item.Id}/editar`} className="button button--ghost button--sm">
                        <PenLine className="size-4" />
                        {item.status === "rascunho" ? "Continuar preenchendo" : "Ver / editar"}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
